import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import Header from 'components/headers/withGreenBack'
import DotsLoading from 'components/loadings/dotsLoading'
import { SectionError } from 'components/states'
import {
    createCashboxBulletin,
    deleteCashboxBulletin,
    getCashboxBulletins,
    updateCashboxBulletin,
} from 'services/cashbox'
import {
    Card,
    Notice,
    PrimaryButton,
    SecondaryButton,
    apiMessage,
} from 'modules/cashbox/ui'
import { useCashbox } from 'modules/cashbox/hooks'

/**
 * تابلوی اعلانات صندوق — short notes the manager pins for members.
 *
 * `sticker` is a small fixed set of decorations; the picker endpoint returns
 * image urls, but the id is all the write endpoints need, so a numeric choice
 * keeps this screen to one request.
 */
export default function CashboxBulletinsPage() {
    const { id } = useParams()
    const queryClient = useQueryClient()
    const { cashbox } = useCashbox(id)

    const { data, isLoading, isError, refetch } = useQuery(
        ['cashbox-bulletins', id],
        getCashboxBulletins,
        { enabled: Boolean(id) }
    )

    const [text, setText] = useState('')
    const [sticker, setSticker] = useState(1)
    const [editingId, setEditingId] = useState<number | null>(null)

    const isOwner = cashbox?.membership?.role === 'own'
    const invalidate = () =>
        queryClient.invalidateQueries(['cashbox-bulletins', id])

    const reset = () => {
        setText('')
        setSticker(1)
        setEditingId(null)
    }

    const save = useMutation({
        mutationFn: () =>
            editingId
                ? updateCashboxBulletin({
                      id: id!,
                      bulletinId: editingId,
                      text: text.trim(),
                      sticker,
                  })
                : createCashboxBulletin({
                      id: id!,
                      text: text.trim(),
                      sticker,
                  }),
        onSuccess: () => {
            toast.success(editingId ? 'اعلان ویرایش شد' : 'اعلان ثبت شد')
            reset()
            invalidate()
        },
        onError: (error: any) => {
            toast.error(apiMessage(error))
        },
    })

    const remove = useMutation({
        mutationFn: (bulletinId: number) =>
            deleteCashboxBulletin({ id: id!, bulletinId }),
        onSuccess: () => {
            toast.success('اعلان حذف شد')
            invalidate()
        },
        onError: (error: any) => {
            toast.error(apiMessage(error))
        },
    })

    if (isLoading) return <DotsLoading />
    if (isError)
        return (
            <div className="bg-white min-h-screen">
                <Header title="تابلوی اعلانات" />
                <div className="p-4">
                    <SectionError onRetry={() => refetch()} />
                </div>
            </div>
        )

    const bulletins = (data ?? []) as Array<ICashboxBulletin>

    return (
        <div className="bg-[#FAFAFA] min-h-screen pb-8">
            <div className="bg-white">
                <Header title="تابلوی اعلانات" />
            </div>

            <div className="px-4 pt-3">
                {isOwner && (
                    <Card className="mb-4">
                        <p className="text-sm font-bold text-black mb-2">
                            {editingId ? 'ویرایش اعلان' : 'اعلان جدید'}
                        </p>
                        <textarea
                            value={text}
                            maxLength={500}
                            rows={3}
                            placeholder="متن اعلان برای اعضای صندوق…"
                            onChange={(event) => setText(event.target.value)}
                            className="w-full rounded-[10px] border border-divider bg-white p-3 text-sm outline-none focus:border-green-1 resize-none"
                        />
                        <div className="flex gap-2 mt-2 mb-3">
                            {[1, 2, 3, 4, 5].map((option) => (
                                <button
                                    key={option}
                                    type="button"
                                    onClick={() => setSticker(option)}
                                    className={`w-9 h-9 rounded-full border text-xs ${
                                        sticker === option
                                            ? 'border-green-1 bg-[#E6F3DD] text-green-1 font-bold'
                                            : 'border-divider bg-white text-text-secondary'
                                    }`}
                                >
                                    {option}
                                </button>
                            ))}
                        </div>
                        <div className="flex gap-2">
                            <PrimaryButton
                                className="h-10"
                                disabled={
                                    text.trim().length === 0 || save.isLoading
                                }
                                onClick={() => save.mutate()}
                            >
                                {editingId ? 'ذخیره' : 'ثبت اعلان'}
                            </PrimaryButton>
                            {editingId && (
                                <SecondaryButton
                                    className="h-10"
                                    onClick={reset}
                                >
                                    انصراف
                                </SecondaryButton>
                            )}
                        </div>
                    </Card>
                )}

                {bulletins.length === 0 ? (
                    <Notice>
                        هنوز اعلانی ثبت نشده است.
                    </Notice>
                ) : (
                    bulletins.map((bulletin) => (
                        <Card key={bulletin.id} className="mb-2">
                            <p className="text-sm text-black whitespace-pre-wrap leading-6">
                                {bulletin.text}
                            </p>
                            {isOwner && (
                                <div className="flex gap-2 mt-3">
                                    <SecondaryButton
                                        className="h-9 text-xs"
                                        onClick={() => {
                                            setEditingId(bulletin.id)
                                            setText(bulletin.text)
                                            setSticker(bulletin.sticker || 1)
                                        }}
                                    >
                                        ویرایش
                                    </SecondaryButton>
                                    <SecondaryButton
                                        className="h-9 text-xs text-[#B3261E] border-[#F5C2BD]"
                                        disabled={remove.isLoading}
                                        onClick={() => {
                                            if (
                                                window.confirm(
                                                    'این اعلان حذف شود؟'
                                                )
                                            )
                                                remove.mutate(bulletin.id)
                                        }}
                                    >
                                        حذف
                                    </SecondaryButton>
                                </div>
                            )}
                        </Card>
                    ))
                )}
            </div>
        </div>
    )
}
